import { Box, Typography } from "@mui/material";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useHrIntegrations from "../../../../api/hooks/useHrIntegrations";
import StatusChip from "../../../../components/chips/StatusChip";
import CustomTooltipBorder from "../../../../components/CustomTooltipBorder";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { PING_STATUS } from "../../../../utils/constants";
import { getPrettyDateTime, useLocalTime } from "../../../../utils/timestamp";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import AdminPermissionsIcon from "./AdminPermissionsIcon";
import EmailPingStatus from "./EmailPingStatus";

const AdminPermissionsTable = ({
  adminUsers = [],
  adminUsersLoading = false,
  gridRef = null,
  onRowClick = null,
  setOpenModal,
  setUserToPing,
  circleColor,
  selectedIdsToApprove,
  setSelectedIdsToApprove,
}) => {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const { isAnyHrIntegrated } = useHrIntegrations();

  const showLocalTime = useLocalTime();

  const hrInfoFields = ["title", "department", "manager_name"];
  const adminUserTool = adminUsers[0]?.tool;
  const onRowSelection = (selection) => {
    setSelectedIdsToApprove(selection);
  };
  const tableColumns = [
    {
      headerName: "Name",
      field: "name",
      align: "left",
      headerAlign: "left",
      flex: 1,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="caption">{params?.value}</Typography>
          </Box>
        );
      },
    },
    {
      headerName: "Title",
      field: "title",
      align: "left",
      headerAlign: "left",
      flex: 1,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="caption">
              {params?.row?.hr_info?.title}
            </Typography>
          </Box>
        );
      },
    },
    {
      headerName: "Department",
      field: "department",
      align: "left",
      headerAlign: "left",
      flex: 1,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="caption">
              {params?.row?.hr_info?.department}
            </Typography>
          </Box>
        );
      },
    },
    {
      headerName: "Last Seen",
      field: "last_seen_in_system",
      align: "center",
      headerAlign: "center",
      flex: 1,
      valueFormatter: (params) =>
        getPrettyDateTime(params?.value?.timestamp_utc, showLocalTime),
      renderCell: (params) => {
        let last_seen_in_system = getPrettyDateTime(
          params?.value?.timestamp_utc,
          showLocalTime
        );

        return (
          <Box>
            <Typography variant="caption">{last_seen_in_system}</Typography>
          </Box>
        );
      },
    },
    {
      headerName: "Privileged Access",
      align: "left",
      headerAlign: "left",
      field: "admin_permissions",
      flex: 1,
      valueGetter: (params) => {
        return params?.row?.admin_permissions;
      },
      valueFormatter: (params) => {
        let permissions = params?.value?.map((admin) => admin?.admin_type);
        return permissions.join(", ");
      },
      renderCell: (params) => {
        return (
          <Box sx={{ whiteSpace: "nowrap" }}>
            <Typography variant="caption">
              <AdminPermissionsIcon
                sincePermissionList={params?.row?.admin_permissions}
              />
            </Typography>
          </Box>
        );
      },
    },
    {
      headerName: "Status",
      field: "ping_status",
      align: "left",
      headerAlign: "left",
      flex: 1,
      valueFormatter: (params) => {
        return params?.value?.status;
      },
      renderCell: (params) => {
        return (
          <>
            <CustomTooltipBorder
              title={
                <EmailPingStatus
                  status_details={params?.value}
                  setOpenModal={setOpenModal}
                  userPermissionDetails={params.row}
                  setUserToPing={setUserToPing}
                />
              }
              borderColor={
                PING_STATUS.PENDING === params?.value.status
                  ? colors.common_white
                  : circleColor[params?.value]
              }
              onMouseEnter={() => {
                trackEvent("user hover on status ping chip");
              }}
            >
              <Box sx={{ whiteSpace: "nowrap" }}>
                <StatusChip
                  circleColor={circleColor[params?.value.status]}
                  label={params?.value.status}
                />
              </Box>
            </CustomTooltipBorder>
          </>
        );
      },
    },
    {
      headerName: "Manager",
      field: "manager_name",
      align: "left",
      headerAlign: "left",
      flex: 1,
      renderCell: (params) => {
        return (
          <Box>
            {
              <Typography variant="caption">
                {params?.row?.hr_info?.manager_name
                  ? params?.row?.hr_info?.manager_name
                  : params?.row?.hr_info?.manager_email}
              </Typography>
            }
          </Box>
        );
      },
    },
  ];

  let filteredColumns = tableColumns.filter((column) => {
    if (!isAnyHrIntegrated && hrInfoFields.includes(column.field)) {
      return false;
    }
    return true;
  });
  if (!IntegrationsMapping[adminUserTool]?.hasUserLastSeen) {
    filteredColumns = filteredColumns.filter((column) => {
      if (column.field === "last_seen_in_system") {
        return false;
      }
      return true;
    });
  }

  return (
    <StyledDataGrid
      style={{ cursor: "pointer" }}
      columns={filteredColumns}
      rows={adminUsers}
      loading={adminUsersLoading}
      apiRef={gridRef}
      onRowClick={onRowClick}
      onRowSelectionModelChange={onRowSelection}
      rowSelectionModel={selectedIdsToApprove}
      pageSizeOptionsOverride={[8, 20, 50]}
      checkboxSelection={true}
      disableSelectionOnClick={true}
    />
  );
};

export default AdminPermissionsTable;
